/**
 * Utility functions for color contrast calculations
 */

/**
 * Converts a hex color to RGB values
 * @param hex - Hex color string (e.g., "#FF0000" or "FF0000")
 * @returns RGB object with r, g, b values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    // Remove # if present
    const cleanHex = hex.replace('#', '');
    
    // Handle 3-digit hex
    if (cleanHex.length === 3) {
        const r = parseInt(cleanHex[0] + cleanHex[0], 16);
        const g = parseInt(cleanHex[1] + cleanHex[1], 16);
        const b = parseInt(cleanHex[2] + cleanHex[2], 16);
        return { r, g, b };
    }
    
    // Handle 6-digit hex
    if (cleanHex.length === 6) {
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        return { r, g, b };
    }
    
    return null;
}

/**
 * Calculates the relative luminance of a color
 * @param r - Red value (0-255)
 * @param g - Green value (0-255)
 * @param b - Blue value (0-255)
 * @returns Relative luminance value
 */
function getRelativeLuminance(r: number, g: number, b: number): number {
    const [rs, gs, bs] = [r, g, b].map(c => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Calculates the contrast ratio between two colors
 * @param color1 - First color (hex string)
 * @param color2 - Second color (hex string)
 * @returns Contrast ratio (1-21)
 */
export function getContrastRatio(color1: string, color2: string): number {
    const rgb1 = hexToRgb(color1);
    const rgb2 = hexToRgb(color2);
    
    if (!rgb1 || !rgb2) {
        return 1; // Default to minimum contrast if colors are invalid
    }
    
    const lum1 = getRelativeLuminance(rgb1.r, rgb1.g, rgb1.b);
    const lum2 = getRelativeLuminance(rgb2.r, rgb2.g, rgb2.b);
    
    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);
    
    return (brightest + 0.05) / (darkest + 0.05);
}

/**
 * Determines if text should be black or white based on background color
 * @param backgroundColor - Background color (hex string)
 * @returns 'black' or 'white'
 */
export function getTextColorForBackground(backgroundColor: string): 'black' | 'white' {
    const whiteContrast = getContrastRatio(backgroundColor, '#FFFFFF');
    const blackContrast = getContrastRatio(backgroundColor, '#000000');
    
    // Use white text if it has better contrast, otherwise use black
    return whiteContrast > blackContrast ? 'white' : 'black';
}

/**
 * Gets button styles with primary color and appropriate text color
 * @param primaryColor - Primary color (hex string)
 * @param isDisabled - Whether the button is disabled
 * @returns Object with background color and text color
 */
export function getButtonStyles(primaryColor: string | null, isDisabled: boolean = false) {
    if (!primaryColor || isDisabled) {
        return {
            backgroundColor: isDisabled ? '#9CA3AF' : undefined,
            color: isDisabled ? '#FFFFFF' : undefined,
        };
    }
    
    const textColor = getTextColorForBackground(primaryColor);
    
    return {
        backgroundColor: primaryColor,
        color: textColor === 'white' ? '#FFFFFF' : '#000000',
    };
}

/**
 * Accent usable as text/border on a surface. Falls back when WCAG AA (4.5:1) fails.
 */
export function getReadableAccentColor(
    accent: string | null | undefined,
    surface: string,
    fallback: string,
    minimumRatio = 4.5,
): string {
    if (!accent) {
        return fallback;
    }

    return getContrastRatio(accent, surface) >= minimumRatio ? accent : fallback;
}
